
import { ActionTypes } from '@/store/actionTypes';
import { defineComponent } from 'vue';
import { MutationTypes } from '@/store/mutationTypes';
import LocationBeggingModal from '@/components/LocationBeggingModal.vue';
import { getCurrentLocation } from '@/shared/helpers';
import useCameraPermission from '@/shared/composables/useCameraPermission';
import { usePermission } from '@vueuse/core';
import { mapGetters } from 'vuex';

export default defineComponent({
    components: { LocationBeggingModal },
    props: {
        company: { type: String, required: true },
        driverCode: { type: String, required: true },
    },
    data: (): {
        displayLocationPrompt: boolean;
    } => {
        return {
            displayLocationPrompt: false,
        };
    },
    setup() {
        const geoLocationPermission = usePermission('geolocation');
        const { watchCameraAndLogPermissionsStatus } = useCameraPermission();

        watchCameraAndLogPermissionsStatus();
        return {
            geoLocationPermission,
        };
    },
    computed: {
        ...mapGetters({
            driver: 'getDriver',
            locationFeatureEnabled: 'driverDeviceLocationFeatureEnabled',
        }),
    },
    methods: {
        promptLocationSharing() {
            this.displayLocationPrompt = true;
        },
        async requestLocationPermission() {
            this.displayLocationPrompt = false;

            try {
                // User granted geolocation permission
                await getCurrentLocation();
                await this.initializeApp();
                await this.beginWatchingDeviceLocation();
            } catch (error) {
                // User denied geolocation permission
                await this.initializeApp();
            }
        },
        async beginWatchingDeviceLocation() {
            await this.$store.dispatch(
                ActionTypes.BEGIN_WATCHING_DEVICE_LOCATION,
                undefined
            );
        },
        async initializeApp() {
            await this.fetchDriver();

            const driver = this.$store.getters.getDriver;

            if (driver?.truckCode.trim()) {
                await this.fetchTruck(driver.truckCode);
            } else {
                await this.$store.commit(MutationTypes.SET_TRUCK, null);
            }
        },
        async fetchDriver() {
            await this.$store.dispatch(ActionTypes.FETCH_DRIVER, {
                company: this.company,
                driverCode: this.driverCode,
            });
        },
        async fetchTruck(truckCode: string) {
            await this.$store.dispatch(ActionTypes.FETCH_TRUCK, {
                company: this.company,
                id: truckCode,
            });
        },
    },
    async beforeCreate() {
        await this.$store.dispatch(ActionTypes.FETCH_FEATURE_FLAGS, {
            driverCode: this.driverCode,
            company: this.company,
        });
        if (
            this.locationFeatureEnabled &&
            this.geoLocationPermission === 'prompt'
        ) {
            this.promptLocationSharing();
        } else {
            await this.initializeApp();
        }
    },
    watch: {
        geoLocationPermission: {
            async handler(permissionState: string) {
                if (
                    this.locationFeatureEnabled &&
                    permissionState === 'granted'
                ) {
                    await this.beginWatchingDeviceLocation();
                }
            },
        },
    },
});
